import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { API, get, post, put } from 'utils/api';
import { logError } from 'utils/log';
import { SUBCAMPAIGN_REVOKED_BY_BRAND, SUBCAMPAIGN_STATE_REVOKED } from 'utils/subcampaign';

import type {
  Action,
  ListMeta,
  ShippingAddress,
  Subcampaign,
  SubcampaignReduxState,
  SubcampaignRevokedBy,
  SubcampaignState,
  ThunkAction
} from 'types';

const LOADING = 'subcampaign/LOADING';
const LOADED = 'subcampaign/LOADED';
const RESET = 'subcampaign/RESET';
const REVOKED = 'subcampaign/REVOKED';
const SHIPPING_ADDRESS_EDITED = 'subcampaign/SHIPPING_ADDRESS_EDITED';

const initialState: SubcampaignReduxState = {
  isLoading: false,
  data: null
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

function subcampaignLoaded(subcampaign): Action {
  return {
    type: LOADED,
    payload: {
      subcampaign
    }
  };
}

export function subcampaignShippingEdited(shippingAddress: ShippingAddress): Action {
  return {
    type: SHIPPING_ADDRESS_EDITED,
    payload: {
      shippingAddress
    }
  };
}

export function resetSubcampaign(): Action {
  return {
    type: RESET,
    payload: {}
  };
}

export function individualSubcampaignRevoked(subcampaignId: number): Action {
  return {
    type: REVOKED,
    payload: {
      subcampaignId
    }
  };
}

export function fetchSubcampaign(
  subcampaignId: number,
  params: {
    [x: string]: boolean
  } = {
    showLoading: true,
    reset: true,
    includeDetails: false
  }
): ThunkAction {
  return async (dispatch) => {
    if (params.showLoading) {
      dispatch(setLoadingStatus(true));
    }

    if (params.reset) {
      dispatch(resetSubcampaign());
    }

    try {
      const subcampaign = await get(API.END_POINTS.SUBCAMPAIGNS.getSubcampaign(subcampaignId, params.includeDetails));
      dispatch(subcampaignLoaded(subcampaign.data));
      return subcampaign.data;
    } catch (error) {
      dispatch(setLoadingStatus(false));
      throw error;
    }
  };
}

export function sendQuote(subcampaign: Subcampaign, quote: string): ThunkAction {
  return async (dispatch) => {
    const url = API.END_POINTS.SUBCAMPAIGNS.sendQuote(subcampaign.offer.id);
    try {
      dispatch(showLoading(url));
      await put(url, {
        data: {
          quote
        }
      }, { suppressGlobalNotification: true });
      dispatch(fetchSubcampaign(subcampaign.id));
    } catch (e) {
      logError(e);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function createListFromSubcampaigns(
  listMeta: ListMeta,
  campaignId: number,
  organizationId: number,
  offerState: string
): ThunkAction {
  return async (dispatch) => {
    const url = API.END_POINTS.SUBCAMPAIGNS.createCollectionForOfferType(campaignId);
    try {
      dispatch(showLoading(url));
      const result = await post(url, {
        data: {
          organizationId,
          offerState,
          label: listMeta.label,
          description: listMeta.description
        }
      }, { suppressGlobalNotification: true });

      return `/collections/creators/${result.data.id}?orgId=${organizationId}`;
    } catch (e) {
      logError(e);
      throw e;
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export default createReducer<SubcampaignReduxState, any>('subcampaign', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state, { subcampaign }) => ({
    ...state,
    data: subcampaign,
    isLoading: false
  }),
  [SHIPPING_ADDRESS_EDITED]: (state, { shippingAddress }) => ({
    ...state,
    data: (state.data)
      ? {
        ...state.data,
        shippingAddress
      }
      : state.data,
    isLoading: false
  }),
  [REVOKED]: (state, { subcampaignId }) => ({
    ...state,
    data: (state.data && state.data.id === subcampaignId)
      ? {
        ...state.data,
        state: SUBCAMPAIGN_STATE_REVOKED as SubcampaignState,
        revokedBy: SUBCAMPAIGN_REVOKED_BY_BRAND as SubcampaignRevokedBy
      }
      : state.data
  })
});
